import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { createTheme, ThemeProvider } from '@mui/material';
import LicensePlate from './Inputs/components/LicensePlate/LicensePlate';
import TextField from './Inputs/components/TextField/TextField';
import Button from './Inputs/components/Button/Button';
import { usePostSearchShop } from './api/client';
import GoogleMaps from './Maps/components/GoogleMaps/GoogleMaps';
import { RepairShop } from './api/models/RepairShop';
import { useAppConfig } from './utils/config-store';
import { useScrollingHelper } from './utils/scrolling-helper';
import spinner from './utils/spinner.gif';
import { ButtonChevron } from './utils/Icons';
import ShopItem from './Shop/components/ShopItem/ShopItem';

enum VisibilityState {
  None,
  Loading,
  Maps,
  Error,
}

const App: FC = () => {
  const [config, setConfig] = useAppConfig();
  const execute = usePostSearchShop();
  const scrollTo = useScrollingHelper();

  const mapRef = useRef<HTMLDivElement>(null);

  const [municipality, setMunicipality] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [repairShops, setRepairShops] = useState<RepairShop[] | null>(null);
  const [visibility, setVisibility] = useState<VisibilityState>(VisibilityState.None);
  const [error, setError] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const shopType = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('type') ? urlParams.get('type') : null;
  }, []);

  const formValid = useMemo(() => !!municipality && !!registrationNumber, [municipality, registrationNumber]);

  useEffect(() => {
    const loadConfig = async () => {
      const defaultConfig = await fetch('/config/app.config.json').then(response => response.json());
      let envConfig = {};

      if (process.env.NODE_ENV === 'development') {
        try {
          envConfig = await fetch('/config/app.config.development.json').then(response => response.json());
        } catch (error) {
          console.warn('Development config not found, using default config');
        }
      }

      const finalConfig = { ...defaultConfig, ...envConfig };
      setConfig({
        apiKey: finalConfig['google-maps-api-key'],
        mapId: finalConfig['google-maps-map-id'],
      });
    };

    loadConfig();
  }, []);

  const markers = useMemo(
    () =>
      repairShops
        ?.filter(r => !!r.latitude && !!r.longitude)
        .map(r => ({ number: r.order + 1, lat: r.latitude, lng: r.longitude })) || [],
    [repairShops]
  );

  useEffect(() => {
    if (visibility === VisibilityState.Maps && !!mapRef.current && markers.length) {
      scrollTo(mapRef);
    }
  }, [visibility, markers, mapRef]);

  const handleSearch = async () => {
    if (!municipality || !registrationNumber) {
      return;
    }
    setExpandedItem(null);

    setVisibility(VisibilityState.Loading);

    execute({ municipality, registrationNumber, ...(shopType === 'glass' ? { windshieldRepair: true } : {}) })
      .then(response => {
        setRepairShops(response?.data?.repairShops?.sort((a, b) => a.order - b.order));
        setError(null);
        setVisibility(VisibilityState.Maps);
      })
      .catch(_ => {
        setError('Virhe korjaamojen haussa.');
        setVisibility(VisibilityState.Error);
      });
  };

  const handleExpand = (item: string) => {
    setExpandedItem(previous => (previous === item ? null : item));
  };

  const theme = createTheme({
    typography: {
      fontFamily: 'soleil, sans-serif',
    },
    palette: {
      primary: {
        main: '#DB393C',
      },
      secondary: {
        main: '#000000',
      },
    },
  });

  return (
    config?.apiKey && (
      <APIProvider apiKey={config?.apiKey}>
        <ThemeProvider theme={theme}>
          <div className="main-container">
            <p className="description">
              Anna ajoneuvosi rekisterinumero ja paikkakunta, jossa haluat korjata ajoneuvosi.
            </p>

            <div className="inputs">
              <LicensePlate onChange={setRegistrationNumber} />

              <TextField label="Paikkakunta" onChange={setMunicipality} required />
            </div>

            <Button onClick={handleSearch} text="Hae korjaamoa" disabled={!formValid} icon={<ButtonChevron />} />
            {visibility === VisibilityState.Loading && <img className="loading-spinner" src={spinner} />}
            {visibility === VisibilityState.Error && error && <div className="error">{error}</div>}

            {visibility === VisibilityState.Maps && !!markers.length && (
              <div className={`results-container-${markers.length}`} ref={mapRef}>
                <div className="results-header">
                  <h3>Hakutulokset ({markers.length})</h3>
                </div>
                <div className="results-map">
                  <GoogleMaps markers={markers} />
                </div>

                <div className="results-table">
                  {repairShops?.map(shop => (
                    <ShopItem
                      key={'list-item-' + (shop.order + 1)}
                      shop={shop}
                      expanded={expandedItem === shop.id}
                      onExpand={handleExpand}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </ThemeProvider>
      </APIProvider>
    )
  );
};

export default App;
